import { useState, useCallback, useRef, Fragment } from 'react';
import { downloadAsDoc } from '../utils/exportDoc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  accentS: 'rgba(217,119,87,0.10)',
  accentB: 'rgba(217,119,87,0.28)',
  green: '#4CAF82',
  greenS: 'rgba(76,175,130,0.12)',
  greenB: 'rgba(76,175,130,0.3)',
  orange: '#F0A050',
  orangeS: 'rgba(240,160,80,0.12)',
  text: '#F0EDE8',
  muted: '#888',
  dim: '#2A2A2A',
};

const CAT_COLORS = {
  'Regulatory & Compliance': '#D97757',
  'Fintech & Banking': '#7F77DD',
  'Technology & API': '#4A9EF5',
  'Product & Features': '#4CAF82',
  'Educational': '#F0A050',
  'Industry Use Cases': '#C76FD4',
  'Comparison & Alternatives': '#E05C5C',
  'Thought Leadership': '#5BC4C4',
};

const STAGES = ['Gap Analysis', 'Create Brief', 'First Draft', 'Make It Yours', 'Export'];

/* API */
async function callClaude(prompt, systemPrompt, useWebSearch = false, maxTokens = 2500) {
  const body = {
    model: 'claude-sonnet-4-6',
    max_tokens: maxTokens,
    messages: [{ role: 'user', content: prompt }],
  };
  if (systemPrompt) body.system = systemPrompt;
  if (useWebSearch) body.tools = [{ type: 'web_search_20250305', name: 'web_search' }];

  const res = await fetch(`${BACKEND_URL}/api/claude`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Server error (${res.status}). The AI took too long to respond. Please try again.`);
  }

  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.content?.map((b) => b.text || '').join('') || '';
}

function parseTopics(raw) {
  try {
    const match = raw.match(/```json\s*([\s\S]*?)```/) || raw.match(/(\[[\s\S]*\])/);
    const jsonStr = match ? match[1] : raw;
    const parsed = JSON.parse(jsonStr.trim());
    return Array.isArray(parsed) ? parsed.map((t, i) => ({ ...t, id: i + 1 })) : [];
  } catch {
    return [];
  }
}

/* Shared UI */
function Spinner({ color = C.accent, size = 22 }) {
  return <div style={{ width: size, height: size, border: `1.5px solid ${C.bdr}`, borderTopColor: color, borderRadius: '50%', animation: 'spin .7s linear infinite', flexShrink: 0 }} />;
}

function PipelineBar({ stage, done, setStage }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
      {STAGES.map((label, i) => {
        const id = i + 1;
        const isDone = done.includes(id);
        const isActive = stage === id;
        const canClick = isDone || done.includes(id - 1) || id === 1;
        return (
          <div key={id} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div
              onClick={() => canClick && setStage(id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 5,
                flex: 1,
                padding: '7px 3px',
                borderRadius: 7,
                cursor: canClick ? 'pointer' : 'default',
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1.5px solid ${isDone ? C.green : isActive ? C.orange : C.bdr}`,
                  background: isDone ? C.greenS : isActive ? C.orangeS : C.card,
                  boxShadow: isDone ? `0 0 0 3px ${C.greenS}` : isActive ? `0 0 0 3px ${C.orangeS}` : 'none',
                  transition: 'all .25s',
                }}
              >
                <span style={{ fontSize: isDone ? 14 : 11, fontWeight: 500, color: isDone ? C.green : isActive ? C.orange : C.muted, fontFamily: 'JetBrains Mono, monospace' }}>
                  {isDone ? '✓' : String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <span style={{ fontSize: 12, fontWeight: isActive || isDone ? 500 : 400, color: isDone ? C.green : isActive ? C.orange : C.muted, textAlign: 'center', whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {i < STAGES.length - 1 && <div style={{ height: 1.5, width: 14, flexShrink: 0, marginTop: -16, borderRadius: 2, background: isDone ? C.green : C.bdr, transition: 'background .3s' }} />}
          </div>
        );
      })}
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div style={{ background: C.accentS, border: `0.5px solid rgba(217,119,87,0.2)`, borderRadius: 8, padding: '10px 14px', fontSize: 14.5, color: C.muted, lineHeight: 1.65, marginBottom: 16 }}>{children}</div>
  );
}

function TopicBanner({ topic }) {
  return (
    <div style={{ background: C.accentS, border: `0.5px solid rgba(217,119,87,0.2)`, borderRadius: 8, padding: '11px 15px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ color: C.accent, fontSize: 17 }}>★</span>
      <div>
        <div style={{ fontSize: 12, color: C.accent, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Selected topic</div>
        <div style={{ fontSize: 15, fontWeight: 500, color: C.text, lineHeight: 1.45 }}>{topic.title}</div>
        {topic.keyword && <div style={{ fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono, monospace', marginTop: 3 }}>{topic.keyword}</div>}
      </div>
    </div>
  );
}

function EditablePanel({ content, onSave, label, status, loading, loadingMsg }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  return (
    <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, overflow: 'hidden', marginTop: 12 }}>
      <div style={{ padding: '9px 13px', borderBottom: `0.5px solid ${C.bdr}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {loading && <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.accent, display: 'inline-block', animation: 'pulse 1.4s infinite' }} />}
          <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {status && !loading && <span style={{ fontSize: 12, color: C.green, fontFamily: 'JetBrains Mono, monospace' }}>{status}</span>}
          {content && !loading && !editing && (
            <button
              onClick={() => {
                setVal(content);
                setEditing(true);
              }}
              style={{ padding: '2px 9px', borderRadius: 5, background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, fontSize: 12, cursor: 'pointer' }}
            >
              Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={() => setEditing(false)} style={{ padding: '2px 9px', borderRadius: 5, background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  onSave(val);
                  setEditing(false);
                }}
                style={{ padding: '2px 9px', borderRadius: 5, background: C.accent, color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
              >
                Save
              </button>
            </>
          )}
        </div>
      </div>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 10 }}>
          <Spinner />
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: C.muted }}>{loadingMsg}</span>
        </div>
      ) : editing ? (
        <>
          <textarea
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            style={{ width: '100%', padding: '14px', background: C.surf, border: 'none', borderTop: `1.5px solid ${C.accent}`, color: C.text, fontSize: 14.5, lineHeight: 1.8, outline: 'none', resize: 'vertical', minHeight: 240 }}
          />
          <div style={{ padding: '9px 13px', borderTop: `0.5px solid ${C.bdr}`, display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
            <button onClick={() => setEditing(false)} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, cursor: 'pointer', fontSize: 13 }}>
              Cancel
            </button>
            <button
              onClick={() => {
                onSave(val);
                setEditing(false);
              }}
              style={{ padding: '5px 12px', borderRadius: 6, background: C.accent, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              Save
            </button>
          </div>
        </>
      ) : (
        <div style={{ padding: '14px', fontSize: 15, lineHeight: 1.85, color: C.text, whiteSpace: 'pre-wrap', minHeight: 90, maxHeight: 340, overflowY: 'auto' }}>{content}</div>
      )}
    </div>
  );
}

function ScanningCard({ url, compCount, step }) {
  const steps = [`Crawling ${url}...`, `Reading your existing posts...`, `Scanning ${compCount} competitor site${compCount !== 1 ? 's' : ''}...`, `Identifying keyword gaps...`, `Ranking by opportunity...`, `Building topic list...`];
  return (
    <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
      <Spinner size={36} />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 17, fontWeight: 500, marginBottom: 8 }}>Running live gap analysis</div>
        <div style={{ fontSize: 14, color: C.muted, fontFamily: 'JetBrains Mono, monospace', animation: 'pulse 1.5s infinite' }}>{steps[step % steps.length]}</div>
      </div>
      <div style={{ width: '100%', maxWidth: 320 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: i < step ? C.greenS : i === step ? C.orangeS : C.dim,
                border: `1px solid ${i < step ? C.green : i === step ? C.orange : C.bdr}`,
                fontSize: 11,
                color: i < step ? C.green : i === step ? C.orange : C.muted,
              }}
            >
              {i < step ? '✓' : i === step ? <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.orange, animation: 'pulse 1s infinite' }} /> : ''}
            </div>
            <span style={{ fontSize: 13, color: i < step ? C.green : i === step ? C.text : C.muted }}>{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ContentGap pipeline (per session) */
export default function ContentGapMain({ session, updateSession, setAnalyzing, showToast }) {
  const { stage, done, url, ind, comps, topics, topic, brief, draft, pol, expert, completed_articles } = session;
  const [loading, setLoading] = useState(false);
  const [scanStep, setScanStep] = useState(0);
  const [humanizePass, setHumanizePass] = useState(0); // 0=idle, 1=first pass, 2=polish pass
  const scanTimer = useRef(null);

  const upd = useCallback((patch) => updateSession(session.id, patch), [session.id, updateSession]);
  const go = (n) => upd({ stage: n });

  const inp = { width: '100%', padding: '9px 11px', background: C.surf, border: `0.5px solid ${C.bdr}`, borderRadius: 7, color: C.text, fontSize: 14.5, outline: 'none', transition: 'border-color .15s' };
  const btnP = { background: C.accent, color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 8, fontSize: 14.5, fontWeight: 500, cursor: 'pointer' };
  const btnG = { background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, padding: '7px 14px', borderRadius: 8, fontSize: 14.5, cursor: 'pointer' };

  const runGap = async () => {
    if (!url.trim() || !ind.trim()) return;
    setAnalyzing(session.id);
    setScanStep(0);
    // Full reset — clear ALL downstream stage data so a fresh analysis truly starts from zero
    upd({
      topics: null,
      gap_analysis_raw: '',
      topic: null,
      brief: '',
      draft: '',
      pol: '',
      expert: '',
      completed_articles: [],
      top_keywords: null,
      scrape_stats: null,
      done: [],
      stage: 1,
      label: url,
    });
    let step = 0;
    scanTimer.current = setInterval(() => {
      step++;
      setScanStep(step);
    }, 2200);

    try {
      const competitorList = comps.filter(Boolean);
      const compStr = competitorList.length ? competitorList.join(', ') : 'top competitors in the same niche';

      // 1. ContentGap scrape — your URL + up to 4 competitors in parallel (real H2/H3/title)
      const urlsToScrape = [url, ...competitorList].slice(0, 5);
      const scrapeRes = await fetch(`${BACKEND_URL}/api/seo/scrape-competitors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ urls: urlsToScrape }),
      })
        .then((r) => (r.ok ? r.json() : { data: [] }))
        .catch(() => ({ data: [] }));

      const scrapeData = scrapeRes.data || [];
      const totalHeadings = scrapeData.reduce((acc, s) => acc + (s.h2s?.length || 0) + (s.h3s?.length || 0), 0);
      upd({ scrape_stats: { sites: scrapeData.length, headings: totalHeadings } });

      // Build a compact "real competitor structure" section from Firecrawl results
      let structureContext = '';
      if (scrapeData.length > 0) {
        const lines = scrapeData.map((s) => {
          const h = [...(s.h2s || []).slice(0, 8), ...(s.h3s || []).slice(0, 4)].join(' | ');
          return `- ${s.url}\n  Title: ${s.title || '(no title)'}\n  Headings: ${h || '(none extracted)'}`;
        });
        structureContext = `\n\nREAL PAGE STRUCTURE (scraped live):\n${lines.join('\n')}\n\nUse these actual headings to identify what topics competitors cover and where ${url} has gaps.`;
      }

      const prompt = `You are an expert SEO content strategist. Perform a REAL-TIME content gap analysis.\n\nMY WEBSITE: ${url}\nINDUSTRY / NICHE: ${ind || '(infer from the website)'}\nCOMPETITORS: ${compStr}${structureContext}\n\nTASK:\n1. Cross-reference the scraped competitor headings (above) with what ${url} currently covers.\n2. Search Google for high-volume, relevant keywords in this niche that have little or no coverage on ${url}.\n3. Identify 12-16 specific content gap topics — real opportunities grounded in the scraped structure and live web data.\n\nReturn ONLY a valid JSON array (no markdown, no extra text) in this exact format:\n[\n  {\n    "title": "Exact blog post title you should write",\n    "keyword": "primary target keyword phrase",\n    "comp": "which competitor covers this",\n    "pri": "HIGH",\n    "cat": "category name"\n  }\n]\n\nPriority: HIGH = strong volume + gap; MEDIUM = moderate; STRATEGIC = high conversion, lower volume.`;

      const raw = await callClaude(prompt, 'You are an SEO analyst. Always return valid JSON only - no preamble, no markdown fences, no explanation.', true);
      clearInterval(scanTimer.current);
      setScanStep(6);
      const parsed = parseTopics(raw);

      // 2. ContentGap keyword data — enrich topics with real volume + CPC + difficulty (India)
      let enriched = parsed;
      if (parsed.length > 0) {
        try {
          const kwRes = await fetch(`${BACKEND_URL}/api/seo/keyword-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ keywords: parsed.map((t) => t.keyword).filter(Boolean) }),
          });
          if (kwRes.ok) {
            const kwBody = await kwRes.json();
            const metrics = kwBody.data || {};
            enriched = parsed.map((t) => {
              const m = metrics[(t.keyword || '').toLowerCase()];
              return m
                ? { ...t, search_volume: m.search_volume, cpc: m.cpc, keyword_difficulty: m.keyword_difficulty }
                : t;
            });
          }
        } catch (_) {
          // Metrics service unavailable — topics still render without metrics
        }
      }

      upd({ topics: enriched.length ? enriched : [], gap_analysis_raw: raw, done: [1], stage: 1 });
    } catch (err) {
      clearInterval(scanTimer.current);
      upd({ topics: [], gap_analysis_raw: `Error: ${err.message}`, done: [], stage: 1 });
    }
    setAnalyzing(null);
    setScanStep(0);
  };

  // Lazy-load top 50 keywords for a competitor when user opens the panel
  const fetchTopKeywords = async () => {
    const competitorList = comps.filter(Boolean);
    const target = competitorList[0] || url;
    if (!target) return;
    upd({ top_keywords: { loading: true } });
    try {
      const res = await fetch(`${BACKEND_URL}/api/seo/top-keywords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ domain: target, limit: 50 }),
      });
      const body = res.ok ? await res.json() : { data: [] };
      upd({ top_keywords: { loading: false, domain: target, items: body.data || [] } });
    } catch (_) {
      upd({ top_keywords: { loading: false, domain: target, items: [] } });
    }
  };

  const startBrief = async (t) => {
    // Reset to a fresh per-topic pipeline: only stage 1 (gap analysis) is done, everything else starts clean
    upd({ topic: t, brief: '', draft: '', pol: '', expert: '', done: [1], stage: 2 });
    setLoading(true);
    try {
      const r = await callClaude(
        `Create a CONCISE SEO content brief (aim for ~500 words total, no more than 800) for:\n\nTITLE: "${t.title}"\nKEYWORD: ${t.keyword}\nINDUSTRY: ${ind || 'inferred from ' + url}\nWEBSITE: ${url}\n\nFormat as 7 short numbered sections, 2-4 bullet points each:\n1. TARGET AUDIENCE (who, pain points)\n2. SEARCH INTENT (what they want)\n3. UNIQUE ANGLE (differentiator vs ${t.comp})\n4. 5 KEY QUESTIONS TO ANSWER\n5. H2/H3 STRUCTURE (5-6 H2s with 1-2 H3s each)\n6. CALL TO ACTION (specific to ${url})\n7. LSI KEYWORDS (8-10 terms)\n\nBe specific and actionable. Do NOT include lengthy stats, vendor lists, or production notes.`,
        'You are an SEO content strategist. Be CONCISE and actionable. Never exceed 800 words.',
        false,
        2500
      );
      upd({ brief: r, done: [1, 2] });
    } catch (err) {
      upd({ brief: `Error generating brief: ${err.message}\n\nThis usually means the AI took too long. Try clicking 'Create brief' again - shorter responses tend to work better.` });
    }
    setLoading(false);
  };

  const genDraft = async () => {
    upd({ draft: '', pol: '', done: [1, 2], stage: 3 });
    setLoading(true);
    try {
      // Trim brief to first ~1000 chars to keep input small + faster generation
      const briefSnippet = (brief || '').slice(0, 1200);
      const r = await callClaude(
        `Write a blog post for ${url}:\n\nTITLE: ${topic.title}\nKEYWORD: ${topic.keyword}\nINDUSTRY: ${ind || 'inferred'}\n\nBRIEF SUMMARY:\n${briefSnippet}\n\nRequirements:\n- TARGET LENGTH: 600-800 words (be focused)\n- Strong hook (NOT "In today's digital age")\n- Short paragraphs (2-3 sentences each)\n- Concrete examples\n- 4-5 specific subheadings\n- Clear CTA for ${url}\n- Expert tone, not AI-like`,
        'You are a senior B2B content writer. Be CONCISE. Stay within 600-800 words. Avoid AI cliches.',
        false,
        2000,
        'claude-haiku-4-5'  // Haiku is ~3x faster, great for blog content
      );
      upd({ draft: r, done: [1, 2, 3] });
    } catch (err) {
      upd({ draft: `Error generating draft: ${err.message}\n\nTry clicking 'Generate first draft' again.` });
    }
    setLoading(false);
  };

  const humanise = async () => {
    upd({ pol: '' });
    setLoading(true);
    setHumanizePass(1);
    const ep = expert
      ? `\n\nEXPERT INPUT TO WEAVE IN AS A PERSONAL STORY/OBSERVATION (use first-person voice, make it feel lived):\n${expert}\n`
      : '';
    try {
      // PASS 1 — Aggressive humanisation
      const pass1Prompt = `Rewrite this content to sound like it was written by an experienced human expert — not AI. Apply ALL of these techniques:

1. Vary sentence length dramatically — mix very short sentences with longer ones. Some sentences should be 3 words. Some should be 25+.

2. Start some sentences with "And", "But", "So" — things AI avoids

3. Add imperfections — casual phrases, rhetorical questions, occasional em-dashes and ellipses

4. Remove all perfect transitions like "Furthermore", "Moreover", "In conclusion" — replace with natural connectors

5. Add a personal or opinionated line — something like "Honestly, most companies get this wrong" or "Here's what nobody tells you"

6. Break up long paragraphs — some paragraphs should be one line only

7. Use contractions everywhere — do not → don't, it is → it's, you are → you're

8. Add one specific example or analogy that feels real and grounded

9. Remove any bullet points that feel like a listicle — convert to flowing prose

10. The final output should feel like a senior marketer wrote this at their desk — confident, slightly opinionated, human.${ep}

CONTENT TO REWRITE:
${draft}`;

      const pass1 = await callClaude(
        pass1Prompt,
        'You are a senior marketer rewriting AI-generated content to feel hand-written. Output ONLY the rewritten article — no preamble, no explanation, no notes.',
        false,
        2500,
        'claude-sonnet-4-6'
      );

      // PASS 2 — Final polish targeting residual AI patterns
      setHumanizePass(2);
      const pass2Prompt = `Read this content and identify any sentences that still sound robotic, generic or AI-generated. Rewrite only those sentences. Specifically look for:
- Sentences that start with "This", "The", "It is important"
- Any phrase that sounds like a textbook
- Perfect parallel structure — break it up
- Any cliché phrases like "In today's digital landscape" — remove them

Return the full article with only those sentences rewritten. Keep everything else identical.

CONTENT:
${pass1}`;

      const pass2 = await callClaude(
        pass2Prompt,
        'You are an AI-detection bypass specialist. You hunt down lingering AI patterns and rewrite only those sentences. Preserve everything else word-for-word. Output ONLY the final article — no preamble, no notes.',
        false,
        2500,
        'claude-sonnet-4-6'
      );

      const finalContent = pass2 || pass1;
      upd({
        pol: finalContent,
        done: [1, 2, 3, 4, 5],
        completed_articles: [
          // Replace any existing entry for the same topic title to avoid duplicates on re-humanise
          ...(completed_articles || []).filter((a) => a.title !== (topic?.title || 'Untitled')),
          {
            id: crypto.randomUUID(),
            title: topic?.title || 'Untitled',
            keyword: topic?.keyword || '',
            content: finalContent,
            created_at: new Date().toISOString(),
          },
        ],
      });
    } catch (err) {
      upd({ pol: `Error humanising: ${err.message}\n\nTry clicking 'Humanise draft' again.` });
    }
    setLoading(false);
    setHumanizePass(0);
  };

  const exportContent = (type) => {
    const content = pol || draft;
    if (type === 'copy') {
      navigator.clipboard.writeText(content);
      showToast('Copied!');
    } else if (type === 'txt') {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
      a.download = `${topic?.title?.slice(0, 40) || 'blog'}.txt`;
      a.click();
      showToast('Downloaded!');
    } else if (type === 'doc') {
      downloadAsDoc(content, topic?.title || 'Blog Post');
      showToast('Word doc downloaded!');
    } else showToast(`${type} - coming soon!`);
  };

  const cats = topics ? [...new Set(topics.map((t) => t.cat))] : [];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '32px 28px 80px' }}>
      {stage > 0 && (
        <PipelineBar
          stage={stage}
          done={done}
          setStage={(n) => {
            const ok = done.includes(n) || done.includes(n - 1) || n === 1;
            if (ok) go(n);
          }}
        />
      )}

      {stage === 0 && (
        <div style={{ animation: 'fadeUp .4s ease', textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
          <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.accent, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 }}>Real-time SEO content gap analysis</div>
          <h1 style={{ fontSize: 36, fontWeight: 600, letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 14 }}>
            Close the Gap.
            <br />
            <span style={{ color: C.accent }}>Claim the Traffic.</span>
          </h1>
          <p style={{ fontSize: 17, color: C.text, lineHeight: 1.5, marginBottom: 12, fontWeight: 400 }}>
            Spy on your competitors' content strategy in real-time.
          </p>
          <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7, maxWidth: 560, marginInline: 'auto', marginBottom: 32 }}>
            Enter your website and competitors. ContentGap will crawl the live sites, find what topics your competitors rank for that you're missing, and build you a prioritised gap list.
          </p>
          <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, padding: 20, marginBottom: 18, textAlign: 'left' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>
                  Your website URL <span style={{ color: C.accent }}>*</span>
                </label>
                <input
                  value={url}
                  onChange={(e) => upd({ url: e.target.value, label: e.target.value || 'New session' })}
                  placeholder="yourdomain.com"
                  style={inp}
                  data-testid="url-input"
                  onFocus={(e) => (e.target.style.borderColor = C.accent)}
                  onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>
                  Industry / niche <span style={{ color: C.accent }}>*</span>
                </label>
                <input value={ind} onChange={(e) => upd({ ind: e.target.value })} placeholder="e.g. eSign SaaS India" style={inp} data-testid="industry-input" onFocus={(e) => (e.target.style.borderColor = C.accent)} onBlur={(e) => (e.target.style.borderColor = C.bdr)} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 7 }}>Competitor URLs</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                {comps.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono, monospace', width: 14, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                    <input
                      value={c}
                      onChange={(e) => {
                        const n = [...comps];
                        n[i] = e.target.value;
                        upd({ comps: n });
                      }}
                      placeholder={`competitor${i + 1}.com`}
                      style={inp}
                      onFocus={(e) => (e.target.style.borderColor = C.accent)}
                      onBlur={(e) => (e.target.style.borderColor = C.bdr)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
            <button onClick={runGap} disabled={!url.trim() || !ind.trim()} data-testid="run-gap-analysis-btn" style={{ ...btnP, padding: '10px 24px', fontSize: 15.5, borderRadius: 9, opacity: (url.trim() && ind.trim()) ? 1 : 0.45, cursor: (url.trim() && ind.trim()) ? 'pointer' : 'not-allowed' }}>
              Run live gap analysis →
            </button>
            <span style={{ fontSize: 13, color: C.muted }}>
              {!url.trim() ? 'Add your website URL to continue' : !ind.trim() ? 'Add your industry/niche to continue' : 'Crawls live sites · ~30-60s'}
            </span>
          </div>
        </div>
      )}

      {stage === 1 && (
        <div style={{ animation: 'fadeUp .35s ease' }}>
          <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>01 - Gap analysis</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-1px', marginBottom: 4 }}>
                Content gaps for <span style={{ color: C.accent }}>{url}</span>
              </h2>
              <p style={{ fontSize: 15, color: C.muted }}>Topics your competitors cover that you're missing - found via live web search.</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => upd({ stage: 0 })}
                title="Edit your website, industry, or competitor URLs"
                data-testid="edit-setup-btn"
                style={{ ...btnG, fontSize: 13 }}
              >
                ✎ Edit setup
              </button>
              <button
                onClick={() => upd({ stage: 0, topics: null, topic: null, brief: '', draft: '', pol: '', expert: '', gap_analysis_raw: '', completed_articles: [], done: [] })}
                title="Reset and re-run a fresh analysis"
                data-testid="rerun-analysis-btn"
                style={{ ...btnG, fontSize: 13 }}
              >
                🔄 Re-run
              </button>
            </div>
          </div>
          {!topics && <ScanningCard url={url} compCount={comps.filter(Boolean).length || 2} step={scanStep} />}
          {topics && topics.length === 0 && (
            <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, padding: 24 }}>
              <div style={{ fontSize: 15, color: C.muted, marginBottom: 12 }}>Could not parse topics. Raw output:</div>
              <pre style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.6 }}>{session.gap_analysis_raw}</pre>
            </div>
          )}
          {topics && topics.length > 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 18 }}>
                {[
                  { n: topics.length, l: 'Gap topics found' },
                  { n: session.scrape_stats?.sites != null ? session.scrape_stats.sites : (comps.filter(Boolean).length || '?'), l: 'Competitors scanned' },
                  { n: topics.filter((t) => t.pri === 'HIGH').length, l: 'High priority' },
                  { n: topics.filter((t) => t.pri === 'STRATEGIC').length, l: 'Strategic' },
                ].map((s) => (
                  <div key={s.l} style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 8, padding: '11px 13px' }}>
                    <div style={{ fontSize: 26, fontWeight: 500, color: C.text, lineHeight: 1, marginBottom: 3 }}>{s.n}</div>
                    <div style={{ fontSize: 13, color: C.muted }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <InfoBox>
                Click <strong style={{ color: C.accent }}>Create brief</strong> on any topic to start the full pipeline.
              </InfoBox>

              {/* Live data badges — show what ContentGap pulled for transparency */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
                {session.scrape_stats && session.scrape_stats.sites > 0 && (
                  <span data-testid="scrape-stats-badge" style={{ fontSize: 11.5, color: C.green, fontFamily: 'JetBrains Mono, monospace', background: C.greenS, border: `0.5px solid ${C.greenB}`, padding: '3px 8px', borderRadius: 4 }}>
                    🕸️ ContentGap scanned {session.scrape_stats.sites} site{session.scrape_stats.sites !== 1 ? 's' : ''} · {session.scrape_stats.headings} headings
                  </span>
                )}
                {topics.some((t) => t.search_volume != null) && (
                  <span style={{ fontSize: 11.5, color: C.accent, fontFamily: 'JetBrains Mono, monospace', background: C.accentS, border: `0.5px solid ${C.accentB}`, padding: '3px 8px', borderRadius: 4 }}>
                    📊 ContentGap · India volume + CPC
                  </span>
                )}
              </div>

              {/* Top 50 keywords (lazy-loaded) */}
              <div style={{ marginBottom: 18, background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 8, overflow: 'hidden' }}>
                <button
                  onClick={() => {
                    if (!session.top_keywords) {
                      fetchTopKeywords();
                    } else {
                      upd({ top_keywords: { ...session.top_keywords, collapsed: !session.top_keywords.collapsed } });
                    }
                  }}
                  data-testid="top-keywords-toggle"
                  style={{ width: '100%', padding: '10px 13px', background: 'transparent', border: 'none', color: C.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14 }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: C.accent, fontSize: 13 }}>🔍</span>
                    <span style={{ fontWeight: 500 }}>Top 50 keywords your competitor ranks for</span>
                    {session.top_keywords?.items && (
                      <span style={{ fontSize: 11.5, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>{session.top_keywords.items.length} found</span>
                    )}
                  </span>
                  <span style={{ fontSize: 14, color: C.muted }}>{session.top_keywords && !session.top_keywords.collapsed ? '▾' : '▸'}</span>
                </button>
                {session.top_keywords && !session.top_keywords.collapsed && (
                  <div style={{ borderTop: `0.5px solid ${C.bdr}`, padding: '10px 13px', maxHeight: 320, overflowY: 'auto' }}>
                    {session.top_keywords.loading && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.muted, fontSize: 13 }}>
                        <Spinner size={14} /> Fetching keywords for {session.top_keywords.domain || 'competitor'}...
                      </div>
                    )}
                    {!session.top_keywords.loading && (session.top_keywords.items || []).length === 0 && (
                      <div style={{ fontSize: 13, color: C.muted }}>
                        No keyword data available for this competitor yet. Try a different competitor domain.
                      </div>
                    )}
                    {!session.top_keywords.loading && (session.top_keywords.items || []).length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 70px 70px 50px', gap: 6, fontSize: 12.5 }}>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 4, borderBottom: `0.5px solid ${C.bdr}` }}>Keyword</div>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 4, borderBottom: `0.5px solid ${C.bdr}`, textAlign: 'right' }}>Vol/mo</div>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 4, borderBottom: `0.5px solid ${C.bdr}`, textAlign: 'right' }}>CPC</div>
                        <div style={{ fontFamily: 'JetBrains Mono, monospace', color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 4, borderBottom: `0.5px solid ${C.bdr}`, textAlign: 'right' }}>KD</div>
                        {session.top_keywords.items.map((k, i) => (
                          <Fragment key={i}>
                            <div style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '5px 0' }} title={k.keyword}>{k.keyword}</div>
                            <div style={{ color: '#AAA', fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', padding: '5px 0' }}>{k.search_volume >= 1000 ? `${(k.search_volume / 1000).toFixed(1)}k` : k.search_volume}</div>
                            <div style={{ color: '#AAA', fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', padding: '5px 0' }}>${Number(k.cpc || 0).toFixed(2)}</div>
                            <div style={{ color: k.keyword_difficulty > 60 ? C.orange : k.keyword_difficulty > 30 ? '#AAA' : C.green, fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', padding: '5px 0' }}>{k.keyword_difficulty}</div>
                          </Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {cats.map((cat) => (
                <div key={cat} style={{ marginBottom: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: CAT_COLORS[cat] || C.accent }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: CAT_COLORS[cat] || C.accent }}>{cat}</span>
                  </div>
                  {topics
                    .filter((t) => t.cat === cat)
                    .map((t) => (
                      <div
                        key={t.id}
                        style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 8, padding: '11px 13px', display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 5, transition: 'background .15s' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#1E1E1E')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = C.card)}
                      >
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontFamily: 'JetBrains Mono, monospace',
                            fontWeight: 500,
                            flexShrink: 0,
                            marginTop: 2,
                            background: t.pri === 'HIGH' ? 'rgba(217,119,87,0.12)' : t.pri === 'STRATEGIC' ? 'rgba(76,175,130,0.12)' : 'rgba(240,160,80,0.12)',
                            color: t.pri === 'HIGH' ? C.accent : t.pri === 'STRATEGIC' ? C.green : C.orange,
                            border: `0.5px solid ${t.pri === 'HIGH' ? 'rgba(217,119,87,0.3)' : t.pri === 'STRATEGIC' ? C.greenB : 'rgba(240,160,80,0.3)'}`,
                          }}
                        >
                          {t.pri}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 500, color: C.text, marginBottom: 3, lineHeight: 1.4 }}>{t.title}</div>
                          <div style={{ fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono, monospace', marginBottom: 2 }}>{t.keyword}</div>
                          {(t.search_volume != null || t.cpc != null || t.keyword_difficulty != null) && (
                            <div data-testid={`metrics-${t.id}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 11.5, color: C.muted, fontFamily: 'JetBrains Mono, monospace', marginBottom: 2 }}>
                              {t.search_volume != null && (
                                <>
                                  <span title="Monthly search volume (India)" style={{ color: t.search_volume > 1000 ? C.green : '#AAA' }}>
                                    📊 {t.search_volume >= 1000 ? `${(t.search_volume / 1000).toFixed(1)}k` : t.search_volume}/mo
                                  </span>
                                </>
                              )}
                              {t.cpc != null && t.cpc > 0 && (
                                <>
                                  <span style={{ color: '#555' }}>·</span>
                                  <span title="Cost per click">💰 ${Number(t.cpc).toFixed(2)} CPC</span>
                                </>
                              )}
                              {t.keyword_difficulty != null && t.keyword_difficulty > 0 && (
                                <>
                                  <span style={{ color: '#555' }}>·</span>
                                  <span title="Keyword difficulty (0-100)" style={{ color: t.keyword_difficulty > 60 ? C.orange : t.keyword_difficulty > 30 ? '#AAA' : C.green }}>
                                    ⚡ KD {t.keyword_difficulty}
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {t.comp && t.comp !== 'None' && (
                            <div style={{ fontSize: 12, color: C.muted }}>
                              Covered by: <span style={{ color: '#AAA' }}>{t.comp}</span>
                            </div>
                          )}
                        </div>
                        <button onClick={() => startBrief(t)} disabled={loading} style={{ ...btnP, padding: '5px 12px', fontSize: 12.5, borderRadius: 6, flexShrink: 0 }}>
                          Create brief
                        </button>
                      </div>
                    ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {stage === 2 && topic && (
        <div style={{ animation: 'fadeUp .35s ease' }}>
          <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>02 - Content brief</div>
          <h2 style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-1px', marginBottom: 14 }}>
            Strategic <span style={{ color: C.accent }}>brief</span>
          </h2>
          <TopicBanner topic={topic} />
          <InfoBox>
            <strong>Review and edit</strong> before generating the draft.
          </InfoBox>
          <EditablePanel content={brief} onSave={(b) => upd({ brief: b })} label="Content brief" status={brief && !loading ? '✓ Ready' : ''} loading={loading} loadingMsg="Researching competitor content & crafting brief..." />
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => go(1)} style={btnG}>
              ← Back
            </button>
            {brief && !loading && (
              <button onClick={genDraft} style={btnP}>
                Generate first draft →
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 3 && topic && (
        <div style={{ animation: 'fadeUp .35s ease' }}>
          <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>03 - First draft</div>
          <h2 style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-1px', marginBottom: 14 }}>
            First <span style={{ color: C.accent }}>draft</span>
          </h2>
          <TopicBanner topic={topic} />
          <InfoBox>
            <strong>Read and edit</strong> anything before humanising.
          </InfoBox>
          <EditablePanel content={draft} onSave={(d) => upd({ draft: d })} label="First draft" status={draft && !loading ? '~950-1100 words' : ''} loading={loading} loadingMsg="Writing your first draft..." />
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => go(2)} style={btnG}>
              ← Back
            </button>
            {draft && !loading && (
              <button onClick={() => go(4)} style={btnP}>
                Make it yours →
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 4 && topic && (
        <div style={{ animation: 'fadeUp .35s ease' }}>
          <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>04 - Make it yours</div>
          <h2 style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-1px', marginBottom: 14 }}>
            Make it <span style={{ color: C.accent }}>yours</span>
          </h2>
          <InfoBox>
            <strong>Your secret weapon.</strong> Add a client story, real stat, or strong opinion.
          </InfoBox>
          {/* SEO Education Tip — why manual editing matters */}
          <div
            data-testid="seo-tip-card"
            style={{
              background: 'linear-gradient(135deg, rgba(76,175,130,0.08), rgba(217,119,87,0.06))',
              border: `0.5px solid ${C.greenB}`,
              borderRadius: 10,
              padding: '14px 16px',
              marginBottom: 16,
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: C.greenS,
                border: `0.5px solid ${C.greenB}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 16,
              }}
            >
              💡
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontFamily: 'JetBrains Mono, monospace', color: C.green, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4, fontWeight: 500 }}>
                Why this stage matters for SEO
              </div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.65, marginBottom: 6 }}>
                Google&apos;s <strong>Helpful Content</strong> and <strong>E-E-A-T</strong> updates penalise generic AI text and reward content with real <em>Experience</em>, <em>Expertise</em>, <em>Authoritativeness</em> and <em>Trust</em>.
              </div>
              <ul style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
                <li>One first-person anecdote = a signal AI can&apos;t fake</li>
                <li>A real number or client outcome beats 10 vague sentences</li>
                <li>A strong, branded opinion is what makes you outrank competitors</li>
              </ul>
              <div style={{ fontSize: 13, color: C.green, marginTop: 8, fontWeight: 500 }}>
                ↓ Drop your story below — even 2 lines will move the needle.
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, color: C.muted, marginBottom: 5 }}>
              Expert input <span style={{ color: C.bdr }}>- optional but powerful</span>
            </label>
            <textarea
              value={expert}
              onChange={(e) => upd({ expert: e.target.value })}
              rows={4}
              placeholder="E.g. We had a client who cut contract TAT from 3 days to 4 hours..."
              style={{ width: '100%', padding: '11px', background: C.surf, border: `0.5px solid ${C.bdr}`, borderRadius: 8, color: C.text, fontSize: 14.5, lineHeight: 1.75, outline: 'none', resize: 'vertical' }}
              onFocus={(e) => (e.target.style.borderColor = C.accent)}
              onBlur={(e) => (e.target.style.borderColor = C.bdr)}
            />
          </div>
          <button onClick={humanise} disabled={loading} data-testid="humanise-btn" style={{ ...btnP, marginBottom: 16 }}>
            {loading ? (humanizePass === 1 ? 'Pass 1/2 running...' : 'Pass 2/2 polishing...') : pol ? 'Re-humanise 🔄' : 'Humanise draft (2 passes) →'}
          </button>
          {(loading || pol) && <EditablePanel content={pol} onSave={(p) => upd({ pol: p })} label="Humanised version" status={pol && !loading ? '✓ Publish ready' : ''} loading={loading} loadingMsg={humanizePass === 1 ? 'Pass 1/2 — rewriting for human voice...' : humanizePass === 2 ? 'Pass 2/2 — polishing residual AI patterns...' : 'Removing AI patterns...'} />}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => go(3)} style={btnG}>
              ← Back
            </button>
            {pol && !loading && (
              <button onClick={() => upd({ done: [...new Set([...done, 5])], stage: 5 })} style={btnP}>
                Export & publish →
              </button>
            )}
          </div>
        </div>
      )}

      {stage === 5 && (
        <div style={{ animation: 'fadeUp .35s ease' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>05 - Export</div>
            <h2 style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-1px' }}>
              Export & <span style={{ color: C.green }}>publish</span>
            </h2>
          </div>
          <div style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ padding: '9px 13px', borderBottom: `0.5px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, textTransform: 'uppercase' }}>Final preview</span>
              <span style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.green }}>✓ Publish ready</span>
            </div>
            <div style={{ padding: '14px', fontSize: 15, lineHeight: 1.85, color: C.text, whiteSpace: 'pre-wrap', maxHeight: 280, overflowY: 'auto' }}>{pol || draft}</div>
          </div>
          <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 9 }}>Export options</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7 }}>
            {[
              { k: 'copy', icon: '📋', n: 'Copy', d: 'Paste anywhere' },
              { k: 'txt', icon: '⬇', n: 'Download .txt', d: 'Plain text file' },
              { k: 'doc', icon: '⬇', n: 'Download .doc', d: 'Open in Word' },
              { k: 'wp', icon: '↗', n: 'WordPress', d: 'Publish as draft' },
              { k: 'wf', icon: '↗', n: 'Webflow', d: 'Publish to CMS' },
              { k: 'new', icon: '+', n: 'New topic', d: 'Back to gap list' },
            ].map((o) => (
              <div
                key={o.k}
                onClick={() => (o.k === 'new' ? upd({ topic: null, brief: '', draft: '', pol: '', expert: '', done: [1], stage: 1 }) : exportContent(o.k))}
                style={{ background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 8, padding: '13px 11px', textAlign: 'center', cursor: 'pointer', transition: 'all .15s' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.green;
                  e.currentTarget.style.background = '#1E1E1E';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.bdr;
                  e.currentTarget.style.background = C.card;
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 5, color: C.muted }}>{o.icon}</div>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, marginBottom: 2 }}>{o.n}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{o.d}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-start' }}>
            <button onClick={() => go(4)} data-testid="export-back-btn" style={btnG}>
              ← Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
